import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Eye, EyeOff, ImagePlus, Plus, Pencil, Trash2, Search, Star, X } from 'lucide-react';
import AdminSection from '../components/AdminSection';
import ImagePicker from '../components/ImagePicker';
import Pagination from '../components/Pagination';
import { usePaged } from '../hooks/use-paged';
import { bulkActivities, fetchDestinations } from '../lib/adminApi';
import { categories } from '../mock';
import { useCurrency } from '../context/CurrencyContext';
import {
  apiEnabled,
  fetchAdminActivities,
  fetchAdminDestinations,
  toActivityPayload,
  createActivity,
  updateActivity,
  deleteActivity,
  saveActivityAvailability,
  saveActivityAddons,
  syncActivityCalendar,
  fetchCalendarFeed,
  LANGS,
} from '../lib/adminActivities';

const EMPTY_FORM = {
  slug: '', title: {}, excerpt: {}, description: {},
  island: 'gran-canaria', category: 'nauticas',
  price: '', original_price: '', price_unit: 'persona', duration: '', location: '',
  image: '', gallery: [], video_url: '', included: [], not_included: [], meeting_point: {},
  ticket_types: [], time_slots: [], cancellation_policy: {}, capacity: '',
  featured: false, booking_enabled: false,
  seo_title: {}, seo_description: {}, seo_keyphrase: '', canonical_url: '', noindex: false,
};

// Tabs of the single-activity editor.
const EDITOR_TABS = [
  { id: 'general', label: 'General' },
  { id: 'contenido', label: 'Contenido e idiomas' },
  { id: 'precios', label: 'Precios y entradas' },
  { id: 'imagenes', label: 'Imágenes' },
  { id: 'seo', label: 'SEO' },
  { id: 'disponibilidad', label: 'Disponibilidad' },
  { id: 'extras', label: 'Extras de pago' },
];

// Read either the API doc shape (i18n objects, island/excerpt) or the static
// frontend shape (plain strings, destination/shortDescription) into the form.
function activityToForm(a) {
  // Keep every language stored in the DB so editing one never drops the others.
  const i18nOf = (v) => {
    if (v && typeof v === 'object') return { ...v };
    return v ? { es: v } : {};
  };
  return {
    slug: a.slug || a.id || '',
    title: i18nOf(a.title),
    excerpt: i18nOf(a.excerpt ?? a.shortDescription),
    description: i18nOf(a.description),
    island: a.island || a.destination || 'gran-canaria',
    category: a.category || 'nauticas',
    price: a.price ?? '', original_price: a.original_price ?? a.originalPrice ?? '',
    price_unit: a.price_unit || a.priceUnit || 'persona',
    duration: a.duration || '', location: a.location || '',
    image: a.image || '', gallery: Array.isArray(a.gallery) ? [...a.gallery] : [],
    video_url: a.video_url || a.videoUrl || '',
    // Bullet lists keep every language so editing one never drops the others.
    included: (a.included || []).map(i18nOf),
    not_included: (a.not_included ?? a.notIncluded ?? []).map(i18nOf),
    meeting_point: i18nOf(a.meeting_point ?? a.meetingPoint),
    // Booking config: these drive the real checkout price, so keep them intact.
    ticket_types: (a.ticket_types ?? a.ticketTypes ?? []).map((t) => ({ ...t })),
    time_slots: [...(a.time_slots ?? a.timeSlots ?? [])],
    cancellation_policy: { ...(a.cancellation_policy ?? a.cancellationPolicy ?? {}) },
    capacity: a.capacity ?? '',
    featured: Boolean(a.featured),
    booking_enabled: Boolean(a.booking_enabled ?? a.bookingEnabled),
    seo_title: i18nOf(a.seo_title),
    seo_description: i18nOf(a.seo_description),
    seo_keyphrase: a.seo_keyphrase || '',
    canonical_url: a.canonical_url || '',
    noindex: Boolean(a.noindex),
  };
}

function activityTitle(a) {
  return (a.title && typeof a.title === 'object' ? a.title.es : a.title) || a.slug || a.id;
}

const inputCls =
  'w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#1fa5a3] focus:ring-4 focus:ring-[#1fa5a3]/10';

// Per-activity availability: capacity per slot, real time slots, blocked dates.
// Saves via the dedicated /availability endpoint (separate from the main form).
function AvailabilityEditor({ slug, activity }) {
  const [cap, setCap] = useState(activity?.capacity_per_slot || activity?.capacity || 12);
  const [slots, setSlots] = useState((activity?.time_slots || []).join(', '));
  const [blackout, setBlackout] = useState((activity?.blackout_dates || []).join(', '));
  const [icals, setIcals] = useState((activity?.ical_urls || []).join('\n'));
  const [msg, setMsg] = useState({ ok: '', err: '' });
  const [syncMsg, setSyncMsg] = useState('');
  const feedQuery = useQuery({ queryKey: ['admin', 'calendar-feed'], queryFn: fetchCalendarFeed });
  const mut = useMutation({
    mutationFn: () => saveActivityAvailability(slug, {
      capacity_per_slot: Number(cap) || 1,
      time_slots: slots.split(',').map((x) => x.trim()).filter(Boolean),
      blackout_dates: blackout.split(',').map((x) => x.trim()).filter(Boolean),
      ical_urls: icals.split('\n').map((x) => x.trim()).filter(Boolean),
    }),
    onSuccess: () => setMsg({ ok: 'Disponibilidad guardada.', err: '' }),
    onError: (e) => setMsg({ ok: '', err: e.message || 'No se pudo guardar.' }),
  });
  const syncMut = useMutation({
    mutationFn: () => syncActivityCalendar(slug),
    onSuccess: (r) => setSyncMsg(`✓ ${r.blocked_count} fechas bloqueadas${r.errors?.length ? ` · ${r.errors.length} error(es)` : ''}`),
    onError: (e) => setSyncMsg(`⚠️ ${e.message || 'No se pudo sincronizar'}`),
  });
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <p className="text-sm font-bold text-[#14213d]">Disponibilidad y cupo</p>
      <p className="mt-0.5 text-xs text-slate-500">Controla plazas por franja, horarios reales y días bloqueados. Se aplica al calendario y al pago.</p>
      <div className="mt-3 space-y-3">
        <div>
          <label className="text-xs font-semibold text-slate-500">Plazas por franja</label>
          <input type="number" min="1" className={`${inputCls} mt-1`} value={cap} onChange={(e) => setCap(e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">Horarios (separados por comas)</label>
          <input className={`${inputCls} mt-1`} value={slots} onChange={(e) => setSlots(e.target.value)} placeholder="10:00, 12:00, 16:00" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">Días bloqueados (YYYY-MM-DD, comas)</label>
          <input className={`${inputCls} mt-1`} value={blackout} onChange={(e) => setBlackout(e.target.value)} placeholder="2026-12-25, 2026-12-31" />
        </div>
        {msg.ok ? <p className="text-xs font-semibold text-emerald-600">{msg.ok}</p> : null}
        {msg.err ? <p className="text-xs font-semibold text-red-600">{msg.err}</p> : null}
        <button type="button" disabled={mut.isPending} onClick={() => { setMsg({ ok: '', err: '' }); mut.mutate(); }}
          className="rounded-full border border-[#1fa5a3] px-4 py-2 text-sm font-semibold text-[#1fa5a3] transition hover:bg-[#1fa5a3] hover:text-white disabled:opacity-60">
          {mut.isPending ? 'Guardando…' : 'Guardar disponibilidad'}
        </button>

        {/* iCal sync */}
        <div className="mt-4 border-t border-slate-200 pt-4">
          <p className="text-sm font-bold text-[#14213d]">Sincronizar calendarios (iCal)</p>
          <label className="mt-2 block text-xs font-semibold text-slate-500">
            Importar (Airbnb / Google / Booking) — una URL .ics por línea
          </label>
          <textarea className={`${inputCls} mt-1 min-h-20`} value={icals} onChange={(e) => setIcals(e.target.value)}
            placeholder="https://www.airbnb.com/calendar/ical/....ics" />
          <p className="mt-1 text-xs text-slate-500">Guarda arriba y luego pulsa sincronizar. Las fechas ocupadas en esos calendarios se bloquean aquí (auto cada 30 min).</p>
          <div className="mt-2 flex items-center gap-3">
            <button type="button" disabled={syncMut.isPending} onClick={() => { setSyncMsg(''); syncMut.mutate(); }}
              className="rounded-full bg-[#14213d] px-4 py-2 text-xs font-semibold text-white transition hover:bg-[#0f1a31] disabled:opacity-60">
              {syncMut.isPending ? 'Sincronizando…' : 'Sincronizar ahora'}
            </button>
            {syncMsg ? <span className="text-xs text-slate-600">{syncMsg}</span> : null}
          </div>

          {feedQuery.data?.feed_url ? (
            <div className="mt-4">
              <label className="block text-xs font-semibold text-slate-500">Exportar — suscribe tus reservas OWA en Google Calendar / Airbnb</label>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 truncate rounded-xl bg-slate-100 px-3 py-2 text-[11px] text-slate-700">{feedQuery.data.feed_url}</code>
                <button type="button" onClick={() => navigator.clipboard.writeText(feedQuery.data.feed_url)}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:border-[#1fa5a3] hover:text-[#1fa5a3]">Copiar</button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// Per-activity paid extras (add-ons): pickup, insurance, gear… Each has a label,
// a price and whether it is charged once per booking or per person.
function AddonsEditor({ slug, activity }) {
  const [rows, setRows] = useState(() =>
    (activity?.addons || []).map((a) => ({ id: a.id, label: a.label, price: String(a.price ?? ''), per_person: !!a.per_person }))
  );
  const [msg, setMsg] = useState({ ok: '', err: '' });
  const mut = useMutation({
    mutationFn: () => saveActivityAddons(
      slug,
      rows
        .filter((r) => r.label.trim())
        .map((r) => ({ id: r.id, label: r.label.trim(), price: Number(r.price) || 0, per_person: r.per_person })),
    ),
    onSuccess: () => setMsg({ ok: 'Extras guardados.', err: '' }),
    onError: (e) => setMsg({ ok: '', err: e.message || 'No se pudo guardar.' }),
  });
  const update = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const add = () => setRows((rs) => [...rs, { label: '', price: '', per_person: false }]);
  const remove = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <p className="text-sm font-bold text-[#14213d]">Extras de pago (add-ons)</p>
      <p className="mt-0.5 text-xs text-slate-500">Complementos opcionales que el cliente puede añadir al reservar (recogida, seguro, equipo…).</p>
      <div className="mt-3 space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <input className={`${inputCls} flex-1 min-w-40`} value={r.label} placeholder="Nombre del extra"
              onChange={(e) => update(i, { label: e.target.value })} />
            <input type="number" min="0" step="0.01" className={`${inputCls} w-24`} value={r.price} placeholder="€"
              onChange={(e) => update(i, { price: e.target.value })} />
            <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
              <input type="checkbox" checked={r.per_person} onChange={(e) => update(i, { per_person: e.target.checked })} />
              por persona
            </label>
            <button type="button" onClick={() => remove(i)}
              className="rounded-full border border-slate-200 p-2 text-red-500 hover:border-red-300" aria-label="Eliminar extra">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        <button type="button" onClick={add}
          className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-[#1fa5a3] hover:text-[#1fa5a3]">
          <Plus className="h-3.5 w-3.5" />Añadir extra
        </button>
        {msg.ok ? <p className="text-xs font-semibold text-emerald-600">{msg.ok}</p> : null}
        {msg.err ? <p className="text-xs font-semibold text-red-600">{msg.err}</p> : null}
        <div>
          <button type="button" disabled={mut.isPending} onClick={() => { setMsg({ ok: '', err: '' }); mut.mutate(); }}
            className="rounded-full border border-[#1fa5a3] px-4 py-2 text-sm font-semibold text-[#1fa5a3] transition hover:bg-[#1fa5a3] hover:text-white disabled:opacity-60">
            {mut.isPending ? 'Guardando…' : 'Guardar extras'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminActivities() {
  const queryClient = useQueryClient();
  const { format } = useCurrency();
  // Read destinations from the DB so newly created ones show up here; fall back
  // to the bundled list when there is no backend.
  const destinationsQuery = useQuery({
    queryKey: ['admin', 'destinations'],
    queryFn: fetchDestinations,
    enabled: apiEnabled,
  });
  const staticDestinations = useMemo(fetchAdminDestinations, []);
  const destinations = useMemo(() => {
    const items = destinationsQuery.data?.items;
    if (!items?.length) return staticDestinations;
    return items.map((d) => ({
      slug: d.slug,
      name: (d.name && typeof d.name === 'object' ? d.name.es : d.name) || d.slug,
    }));
  }, [destinationsQuery.data, staticDestinations]);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [islandFilter, setIslandFilter] = useState('all');
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingSlug, setEditingSlug] = useState(null);
  const [view, setView] = useState('list');   // 'list' | 'editor'
  const [tab, setTab] = useState('general');
  const [msg, setMsg] = useState({ ok: '', err: '' });
  const [selected, setSelected] = useState([]);
  const [bulkAction, setBulkAction] = useState('activate');
  const [bulkValue, setBulkValue] = useState('');
  const [picker, setPicker] = useState(null); // 'hero' | 'gallery' | null

  // 'all' | 'active' | 'hidden' — hidden ones must stay reachable to re-enable them.
  const [visibility, setVisibility] = useState('all');
  const activeFilter = visibility === 'all' ? undefined : visibility === 'active';

  const listQuery = useQuery({
    queryKey: ['admin', 'activities', visibility],
    queryFn: () => fetchAdminActivities(activeFilter),
  });
  const items = listQuery.data?.items || [];

  const [lang, setLang] = useState('es');
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));
  // Edit one language of an i18n field without touching the rest.
  const setI18n = (field, value) =>
    setForm((c) => ({ ...c, [field]: { ...c[field], [lang]: value } }));
  // cancellation_policy.short/details are i18n (dicts); read/write the active
  // language while tolerating legacy plain strings.
  const cpText = (field) => {
    const v = form.cancellation_policy?.[field];
    if (v && typeof v === 'object') return v[lang] || '';
    return typeof v === 'string' ? v : '';
  };
  const setCpI18n = (field, value) =>
    setForm((c) => {
      const cur = c.cancellation_policy?.[field];
      const base = cur && typeof cur === 'object' ? cur : (cur ? { es: cur } : {});
      return { ...c, cancellation_policy: { ...c.cancellation_policy, [field]: { ...base, [lang]: value } } };
    });

  const backToList = () => { setView('list'); setForm(EMPTY_FORM); setEditingSlug(null); };
  const startEdit = (a) => { setForm(activityToForm(a)); setEditingSlug(a.slug || a.id); setTab('general'); setLang('es'); setView('editor'); setMsg({ ok: '', err: '' }); };
  const startCreate = () => { setForm(EMPTY_FORM); setEditingSlug(null); setTab('general'); setLang('es'); setView('editor'); setMsg({ ok: '', err: '' }); };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'activities'] });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = toActivityPayload(form, { isNew: !editingSlug });
      return editingSlug ? updateActivity(editingSlug, payload) : createActivity(payload);
    },
    onSuccess: async () => {
      // Stay in the editor so the owner can keep tuning tabs; a brand-new activity
      // becomes an edit (so its slug is fixed and Disponibilidad/Extras unlock).
      setMsg({ ok: editingSlug ? 'Actividad actualizada.' : 'Actividad creada.', err: '' });
      if (!editingSlug) setEditingSlug(form.slug.trim());
      await invalidate();
    },
    onError: (e) => setMsg({ ok: '', err: e.message || 'No se pudo guardar.' }),
  });

  const needsValue = ['price_percent', 'price_delta', 'price_set'].includes(bulkAction);

  const bulkMutation = useMutation({
    mutationFn: () =>
      bulkActivities(selected, bulkAction, needsValue ? Number(bulkValue) : undefined),
    onSuccess: async (res) => {
      setMsg({ ok: `${res.modified} de ${res.matched} actividades actualizadas.`, err: '' });
      setSelected([]);
      setBulkValue('');
      await invalidate();
    },
    onError: (e) => setMsg({ ok: '', err: e.message || 'No se pudo aplicar la acción masiva.' }),
  });

  // Single-activity visibility toggle, reusing the same bulk endpoint.
  const toggleActiveMutation = useMutation({
    mutationFn: ({ slug, active }) => bulkActivities([slug], active ? 'deactivate' : 'activate'),
    onSuccess: async () => { setMsg({ ok: 'Visibilidad actualizada.', err: '' }); await invalidate(); },
    onError: (e) => setMsg({ ok: '', err: e.message || 'No se pudo cambiar la visibilidad.' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (slug) => deleteActivity(slug),
    onSuccess: async () => { setMsg({ ok: 'Actividad eliminada.', err: '' }); await invalidate(); if (view === 'editor') backToList(); },
    onError: (e) => setMsg({ ok: '', err: e.message || 'No se pudo eliminar.' }),
  });

  const filtered = items.filter((a) => {
    const matchesSearch = activityTitle(a).toLowerCase().includes(search.trim().toLowerCase());
    const matchesCategory = categoryFilter === 'all' || a.category === categoryFilter;
    const matchesIsland = islandFilter === 'all' || (a.island || a.destination) === islandFilter;
    return matchesSearch && matchesCategory && matchesIsland;
  });
  const { pageItems: pagedActivities, page, setPage, pageCount, pageSize, setPageSize } = usePaged(filtered, 20);

  const submit = (e) => {
    e.preventDefault();
    if (!apiEnabled) { setMsg({ ok: '', err: 'Backend no conectado: define REACT_APP_API_BASE_URL para guardar.' }); return; }
    if (!form.slug.trim() || !(form.title?.es || '').trim()) {
      setMsg({ ok: '', err: 'El slug (pestaña General) y el título en español (pestaña Contenido) son obligatorios.' });
      return;
    }
    saveMutation.mutate();
  };

  const activityDoc = items.find((a) => (a.slug || a.id) === editingSlug);
  const isFormTab = ['general', 'contenido', 'precios', 'imagenes', 'seo'].includes(tab);

  // Live preview of the online charge (full price vs deposit) for the ticket editor.
  const priceSummary = (form.ticket_types || []).reduce(
    (acc, t) => {
      const price = Number(t.price) || 0;
      const dep = t.deposit === '' || t.deposit == null ? 0 : Number(t.deposit) || 0;
      acc.hasDeposit = acc.hasDeposit || dep > 0;
      return acc;
    },
    { hasDeposit: false }
  );

  const tabBtn = (id, label) => (
    <button
      key={id}
      type="button"
      onClick={() => setTab(id)}
      className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
        tab === id ? 'bg-[#1fa5a3] text-white' : 'border border-slate-200 text-slate-600 hover:border-[#1fa5a3] hover:text-[#1fa5a3]'
      }`}
    >
      {label}
    </button>
  );

  return (
    <AdminSection
      eyebrow="Admin"
      title="Actividades"
      description="Crea, edita y elimina actividades. Se guardan en la base de datos; el precio de reserva lo sigue validando el servidor."
      widthClassName="max-w-7xl"
      actions={(
        <div className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
          {listQuery.data?.source === 'api' ? 'Fuente: API' : 'Fuente: estática'}
        </div>
      )}
    >
      {!apiEnabled ? (
        <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          El backend no está conectado (falta <code>REACT_APP_API_BASE_URL</code>). Se muestra la lista en modo lectura;
          crear/editar/eliminar se activará al desplegar la API.
        </div>
      ) : null}

      {msg.ok ? <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{msg.ok}</div> : null}
      {msg.err ? <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{msg.err}</div> : null}

      {view === 'list' ? (
        /* ---------------- LIST VIEW ---------------- */
        <section className="min-w-0 rounded-[28px] border border-white/70 bg-white/90 p-6 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-2xl font-bold text-[#14213d]">{filtered.length} actividades</h2>
              <p className="mt-1 text-sm text-slate-500">Haz clic en una actividad para ajustar toda su información.</p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <label className="relative block sm:w-64">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por título"
                  className="w-full rounded-full border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 text-sm text-slate-900 placeholder:text-slate-500 outline-none focus:border-[#1fa5a3]" />
              </label>
              <button type="button" onClick={startCreate} disabled={!apiEnabled}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1fa5a3] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#188b89] disabled:opacity-40">
                <Plus className="h-4 w-4" />Nueva actividad
              </button>
            </div>
          </div>

          {/* Visibility tabs — hidden activities live here so they can be re-enabled */}
          <div className="mt-4 flex flex-wrap gap-2">
            {[
              { id: 'all', label: 'Todas', count: listQuery.data ? (listQuery.data.activeCount ?? 0) + (listQuery.data.inactiveCount ?? 0) : null },
              { id: 'active', label: 'Publicadas', count: listQuery.data?.activeCount },
              { id: 'hidden', label: 'Ocultas', count: listQuery.data?.inactiveCount },
            ].map((vt) => (
              <button
                key={vt.id}
                type="button"
                onClick={() => { setVisibility(vt.id); setSelected([]); }}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                  visibility === vt.id
                    ? 'bg-[#14213d] text-white'
                    : 'border border-slate-200 text-slate-600 hover:border-[#1fa5a3] hover:text-[#1fa5a3]'
                }`}
              >
                {vt.label}
                {typeof vt.count === 'number' ? (
                  <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${
                    visibility === vt.id ? 'bg-white/20' : 'bg-slate-100 text-slate-600'
                  }`}>{vt.count}</span>
                ) : null}
              </button>
            ))}
          </div>

          {/* Category / island filters */}
          <div className="mt-3 flex flex-wrap gap-2">
            <select
              value={categoryFilter}
              onChange={(e) => { setCategoryFilter(e.target.value); setSelected([]); }}
              className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 outline-none focus:border-[#1fa5a3]"
            >
              <option value="all">Todas las categorías</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select
              value={islandFilter}
              onChange={(e) => { setIslandFilter(e.target.value); setSelected([]); }}
              className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 outline-none focus:border-[#1fa5a3]"
            >
              <option value="all">Todos los destinos</option>
              {destinations.map((d) => <option key={d.slug} value={d.slug}>{d.name}</option>)}
            </select>
            {categoryFilter !== 'all' || islandFilter !== 'all' ? (
              <button
                type="button"
                onClick={() => { setCategoryFilter('all'); setIslandFilter('all'); setSelected([]); }}
                className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-500 transition hover:border-red-300 hover:text-red-500"
              >
                <X className="h-3.5 w-3.5" />Quitar filtros
              </button>
            ) : null}
          </div>

          {listQuery.isError ? <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">No se pudo cargar la lista.</div> : null}

          {/* Bulk action bar */}
          <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex items-center gap-2 text-sm font-semibold text-[#14213d]">
                <input
                  type="checkbox"
                  checked={filtered.length > 0 && selected.length === filtered.length}
                  onChange={(e) => setSelected(e.target.checked ? filtered.map((a) => a.slug || a.id) : [])}
                />
                Seleccionar todo
              </label>
              <span className="text-sm text-slate-600">{selected.length} seleccionadas</span>

              <select
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
                className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 outline-none focus:border-[#1fa5a3]"
              >
                <option value="activate">Activar</option>
                <option value="deactivate">Desactivar</option>
                <option value="feature">Marcar destacada</option>
                <option value="unfeature">Quitar destacada</option>
                <option value="price_percent">Cambiar precio (%)</option>
                <option value="price_delta">Sumar/restar € al precio</option>
                <option value="price_set">Fijar precio €</option>
              </select>

              {needsValue ? (
                <input
                  type="number" step="0.01"
                  value={bulkValue}
                  onChange={(e) => setBulkValue(e.target.value)}
                  placeholder={bulkAction === 'price_percent' ? '+10 o -15' : '€'}
                  className="w-28 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-500 outline-none focus:border-[#1fa5a3]"
                />
              ) : null}

              <button
                type="button"
                disabled={!apiEnabled || !selected.length || (needsValue && bulkValue === '') || bulkMutation.isPending}
                onClick={() => {
                  const label = { activate: 'activar', deactivate: 'desactivar', feature: 'destacar', unfeature: 'quitar de destacadas' }[bulkAction] || 'cambiar el precio de';
                  if (window.confirm(`¿Seguro que quieres ${label} ${selected.length} actividades?`)) bulkMutation.mutate();
                }}
                className="rounded-full bg-[#14213d] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[#0f1a31] disabled:opacity-40"
              >
                {bulkMutation.isPending ? 'Aplicando…' : 'Aplicar'}
              </button>
            </div>
            {bulkAction === 'price_percent' ? (
              <p className="mt-2 text-xs text-slate-500">Usa un número positivo para subir (+10 = +10%) o negativo para bajar (-15 = −15%).</p>
            ) : null}
          </div>

          <div className="mt-5 divide-y divide-slate-100">
            {pagedActivities.map((a) => {
              const slug = a.slug || a.id;
              const isActive = a.active !== false;
              return (
                <div key={slug} className={`flex items-center gap-3 py-3 ${isActive ? '' : 'rounded-xl bg-amber-50/60 px-2'}`}>
                  <input
                    type="checkbox"
                    checked={selected.includes(slug)}
                    onChange={(e) => setSelected((c) => (e.target.checked ? [...c, slug] : c.filter((s) => s !== slug)))}
                    className="flex-shrink-0"
                  />
                  {/* Click the row to open the single-activity editor */}
                  <button type="button" onClick={() => startEdit(a)} className="flex min-w-0 flex-1 items-center gap-3 rounded-xl px-1 py-1 text-left transition hover:bg-slate-50">
                    <img src={a.image} alt="" className={`h-12 w-16 flex-shrink-0 rounded-lg object-cover bg-slate-100 ${isActive ? '' : 'grayscale'}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="truncate text-sm font-semibold text-[#14213d]">{activityTitle(a)}</p>
                        {!isActive ? (
                          <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800">
                            <EyeOff className="h-3 w-3" />Oculta
                          </span>
                        ) : null}
                      </div>
                      <p className="truncate text-xs text-slate-500">
                        {a.island || a.destination} · {a.category} · {format(a.price)}
                        {a.featured ? <span className="ml-1 inline-flex items-center gap-0.5 text-[#c8a25a]"><Star className="h-3 w-3 fill-[#c8a25a]" />destacada</span> : null}
                      </p>
                    </div>
                    <Pencil className="h-4 w-4 flex-shrink-0 text-slate-300" />
                  </button>
                  <button type="button" disabled={!apiEnabled} onClick={() => toggleActiveMutation.mutate({ slug, active: isActive })}
                    className="rounded-full border border-slate-200 p-2 text-slate-600 hover:border-[#1fa5a3] hover:text-[#1fa5a3] disabled:opacity-40"
                    aria-label={isActive ? 'Ocultar' : 'Mostrar'} title={isActive ? 'Ocultar en la web' : 'Mostrar en la web'}>
                    {isActive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  <button type="button" disabled={!apiEnabled || deleteMutation.isPending}
                    onClick={() => { if (window.confirm(`¿Eliminar "${activityTitle(a)}"?`)) deleteMutation.mutate(slug); }}
                    className="rounded-full border border-slate-200 p-2 text-red-500 hover:border-red-300 disabled:opacity-40" aria-label="Eliminar"><Trash2 className="h-4 w-4" /></button>
                </div>
              );
            })}
            {filtered.length === 0 && !listQuery.isLoading ? <p className="py-8 text-center text-sm text-slate-500">Sin resultados.</p> : null}
            <Pagination page={page} pageCount={pageCount} onPageChange={setPage} pageSize={pageSize} onPageSizeChange={setPageSize} pageSizes={[20, 50, 100]} />
          </div>
        </section>
      ) : (
        /* ---------------- SINGLE-ACTIVITY EDITOR ---------------- */
        <section className="min-w-0 rounded-[28px] border border-white/70 bg-white/90 p-6 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <button type="button" onClick={backToList} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-[#14213d] transition hover:border-[#1fa5a3] hover:text-[#1fa5a3]">
              <ArrowLeft className="h-4 w-4" />Volver a la lista
            </button>
            {editingSlug ? (
              <a href={`/activity/${editingSlug}`} target="_blank" rel="noreferrer" className="text-sm font-semibold text-[#1fa5a3] hover:underline">Ver en la web ↗</a>
            ) : null}
          </div>

          <h2 className="mt-4 text-2xl font-bold text-[#14213d]">
            {editingSlug ? activityTitle(activityDoc || form) : 'Nueva actividad'}
          </h2>
          {editingSlug ? <p className="text-sm text-slate-500">{editingSlug}</p> : null}

          {/* Tabs */}
          <div className="mt-4 flex flex-wrap gap-2 border-b border-slate-100 pb-4">
            {EDITOR_TABS.map((tb) => tabBtn(tb.id, tb.label))}
          </div>

          {isFormTab ? (
            <form onSubmit={submit} className="mt-5 space-y-4">
              {/* ---- GENERAL ---- */}
              {tab === 'general' ? (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Slug (URL única)</label>
                    <input className={`${inputCls} mt-1`} placeholder="ej. jet-ski-safari" value={form.slug}
                      onChange={(e) => set('slug', e.target.value)} disabled={Boolean(editingSlug)} required />
                    {editingSlug ? <p className="mt-1 text-[11px] text-slate-500">El slug no se puede cambiar una vez creada la actividad.</p> : null}
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Isla / destino</label>
                      <select className={`${inputCls} mt-1`} value={form.island} onChange={(e) => set('island', e.target.value)}>
                        {destinations.map((d) => <option key={d.slug} value={d.slug}>{d.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Categoría</label>
                      <select className={`${inputCls} mt-1`} value={form.category} onChange={(e) => set('category', e.target.value)}>
                        {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Ubicación</label>
                      <input className={`${inputCls} mt-1`} placeholder="ej. Puerto Rico, Gran Canaria" value={form.location} onChange={(e) => set('location', e.target.value)} />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Duración</label>
                      <input className={`${inputCls} mt-1`} placeholder="ej. 4 horas" value={form.duration} onChange={(e) => set('duration', e.target.value)} />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <label className="inline-flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.featured} onChange={(e) => set('featured', e.target.checked)} />Destacada en la web</label>
                    <label className="inline-flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.booking_enabled} onChange={(e) => set('booking_enabled', e.target.checked)} />Reserva online (SumUp)</label>
                  </div>
                </div>
              ) : null}

              {/* ---- CONTENIDO E IDIOMAS ---- */}
              {tab === 'contenido' ? (
                <div className="rounded-2xl border border-slate-200 p-4">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {LANGS.map((l) => {
                      const filled = Boolean((form.title?.[l.id] || '').trim());
                      return (
                        <button key={l.id} type="button" onClick={() => setLang(l.id)} title={l.name}
                          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition ${
                            lang === l.id ? 'bg-[#14213d] text-white' : 'border border-slate-200 text-slate-600 hover:border-[#1fa5a3] hover:text-[#1fa5a3]'
                          }`}>
                          {l.label}
                          <span className={`h-1.5 w-1.5 rounded-full ${filled ? 'bg-[#1fa5a3]' : lang === l.id ? 'bg-white/40' : 'bg-slate-300'}`} />
                        </button>
                      );
                    })}
                    <span className="ml-auto text-xs text-slate-500">Editando: {LANGS.find((l) => l.id === lang)?.name}</span>
                  </div>

                  <div className="mt-3 space-y-3">
                    <input className={inputCls} placeholder={`Título (${lang.toUpperCase()})${lang === 'es' ? ' · obligatorio' : ''}`}
                      value={form.title?.[lang] || ''} onChange={(e) => setI18n('title', e.target.value)} />
                    <textarea className={`${inputCls} min-h-20`} placeholder={`Descripción corta (${lang.toUpperCase()})`}
                      value={form.excerpt?.[lang] || ''} onChange={(e) => setI18n('excerpt', e.target.value)} />
                    <div>
                      <textarea className={`${inputCls} min-h-40`} placeholder={`Descripción larga (${lang.toUpperCase()})`}
                        value={form.description?.[lang] || ''} onChange={(e) => setI18n('description', e.target.value)} />
                      <p className="mt-1 text-[11px] text-slate-500">
                        Estructura con encabezados para el SEO: usa <code>## Subtítulo</code>, listas con <code>- punto</code> y <code>**negrita**</code>.
                      </p>
                    </div>
                  </div>

                  {[
                    { field: 'included', label: 'Qué incluye', hint: 'Un punto por línea', accent: 'text-emerald-600' },
                    { field: 'not_included', label: 'No incluye', hint: 'Lo que el cliente paga aparte', accent: 'text-red-500' },
                  ].map(({ field, label, hint, accent }) => (
                    <div key={field} className="mt-4 border-t border-slate-100 pt-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className={`text-xs font-bold uppercase tracking-[0.16em] ${accent}`}>{label}</p>
                          <p className="text-[11px] text-slate-500">{hint}</p>
                        </div>
                        <button type="button" onClick={() => set(field, [...(form[field] || []), { [lang]: '' }])}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-[#14213d] transition hover:border-[#1fa5a3] hover:text-[#1fa5a3]">
                          <Plus className="h-3.5 w-3.5" />Añadir
                        </button>
                      </div>
                      {(form[field] || []).length ? (
                        <div className="mt-2.5 space-y-2">
                          {(form[field] || []).map((item, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <input className={inputCls} placeholder={`Punto ${i + 1} (${lang.toUpperCase()})`}
                                value={item?.[lang] || ''}
                                onChange={(e) => set(field, form[field].map((it, j) => (j === i ? { ...it, [lang]: e.target.value } : it)))} />
                              <button type="button" title="Quitar" onClick={() => set(field, form[field].filter((_, j) => j !== i))}
                                className="flex-shrink-0 rounded-xl border border-slate-200 p-2.5 text-red-500 transition hover:border-red-300">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-2 text-xs text-slate-500">Sin puntos todavía.</p>
                      )}
                    </div>
                  ))}

                  <div className="mt-4 border-t border-slate-100 pt-4">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Punto de encuentro</p>
                    <input className={`${inputCls} mt-2`} placeholder={`Dónde queda el cliente (${lang.toUpperCase()})`}
                      value={form.meeting_point?.[lang] || ''} onChange={(e) => setI18n('meeting_point', e.target.value)} />
                  </div>

                  <div className="mt-4 border-t border-slate-100 pt-4">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Política de cancelación</p>
                    <div className="mt-2.5 space-y-2">
                      <input className={inputCls} type="number" min="0" placeholder="Horas de antelación (ej. 24)"
                        value={form.cancellation_policy?.noticeHours ?? ''}
                        onChange={(e) => set('cancellation_policy', { ...form.cancellation_policy, noticeHours: e.target.value })} />
                      <input className={inputCls} placeholder={`Resumen (${lang.toUpperCase()})`} value={cpText('short')} onChange={(e) => setCpI18n('short', e.target.value)} />
                      <textarea className={`${inputCls} min-h-20`} placeholder={`Detalles (${lang.toUpperCase()})`} value={cpText('details')} onChange={(e) => setCpI18n('details', e.target.value)} />
                    </div>
                  </div>

                  <p className="mt-3 text-xs text-slate-500">Los idiomas que no rellenes se muestran en la web con el texto en inglés o español.</p>
                </div>
              ) : null}

              {/* ---- PRECIOS Y ENTRADAS ---- */}
              {tab === 'precios' ? (
                <div className="space-y-4">
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Precio «desde» €</label>
                      <input className={`${inputCls} mt-1`} type="number" min="0" step="0.01" placeholder="Precio €" value={form.price} onChange={(e) => set('price', e.target.value)} required />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Antes € (tachado)</label>
                      <input className={`${inputCls} mt-1`} type="number" min="0" step="0.01" placeholder="opcional" value={form.original_price} onChange={(e) => set('original_price', e.target.value)} />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Unidad</label>
                      <input className={`${inputCls} mt-1`} placeholder="persona / grupo privado" value={form.price_unit} onChange={(e) => set('price_unit', e.target.value)} />
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 p-4">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#14213d]">Tipos de entrada · precio real y depósito</p>
                    <p className="mt-1 text-[11px] text-slate-500">
                      El <strong>precio real</strong> es el que se muestra en la web. El <strong>depósito</strong> es lo que se cobra online al reservar;
                      el resto lo paga el cliente a bordo/in situ. Deja el depósito en <strong>0</strong> para cobrar el precio completo online.
                    </p>
                    <p className="mt-1 text-[11px] text-slate-500">
                      El nombre visible se traduce por idioma — cambia el idioma en la pestaña <strong>Contenido e idiomas</strong> y vuelve aquí para editarlo en cada uno. Ahora mismo editando: <strong>{lang.toUpperCase()}</strong>.
                    </p>

                    <div className="mt-3 flex items-center justify-between gap-3">
                      <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Entradas</p>
                      <button type="button"
                        onClick={() => set('ticket_types', [...(form.ticket_types || []), { id: '', label: '', price: '', deposit: '', min: 0, max: 12, default: 0, seats: 1 }])}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-[#14213d] transition hover:border-[#1fa5a3] hover:text-[#1fa5a3]">
                        <Plus className="h-3.5 w-3.5" />Añadir
                      </button>
                    </div>

                    {(form.ticket_types || []).length ? (
                      <div className="mt-2.5 space-y-3">
                        {(form.ticket_types || []).map((t, i) => {
                          const upd = (patch) => set('ticket_types', form.ticket_types.map((it, j) => (j === i ? { ...it, ...patch } : it)));
                          return (
                            <div key={i} className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                              <div className="flex items-center gap-2">
                                <input className={inputCls} placeholder={`Nombre visible (ej. Adultos) (${lang.toUpperCase()})`}
                                  value={(typeof t.label === 'object' && t.label ? t.label[lang] : lang === 'es' ? t.label : '') || ''}
                                  onChange={(e) => upd({ label: { ...(typeof t.label === 'object' && t.label ? t.label : { es: t.label || '' }), [lang]: e.target.value } })} />
                                <button type="button" title="Quitar" onClick={() => set('ticket_types', form.ticket_types.filter((_, j) => j !== i))}
                                  className="flex-shrink-0 rounded-xl border border-slate-200 bg-white p-2.5 text-red-500 transition hover:border-red-300">
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-5">
                                <label className="text-[11px] font-semibold text-slate-500">id
                                  <input className={`${inputCls} mt-0.5`} placeholder="adult" value={t.id || ''} onChange={(e) => upd({ id: e.target.value })} />
                                </label>
                                <label className="text-[11px] font-semibold text-slate-500">Precio real €
                                  <input className={`${inputCls} mt-0.5`} type="number" min="0" step="0.01" placeholder="79.21" value={t.price ?? ''} onChange={(e) => upd({ price: e.target.value })} />
                                </label>
                                <label className="text-[11px] font-semibold text-[#1fa5a3]">Depósito €
                                  <input className={`${inputCls} mt-0.5`} type="number" min="0" step="0.01" placeholder="0 = completo" value={t.deposit ?? ''} onChange={(e) => upd({ deposit: e.target.value })} />
                                </label>
                                <label className="text-[11px] font-semibold text-slate-500">Mín.
                                  <input className={`${inputCls} mt-0.5`} type="number" min="0" placeholder="0" value={t.min ?? 0} onChange={(e) => upd({ min: e.target.value })} />
                                </label>
                                <label className="text-[11px] font-semibold text-slate-500">Máx.
                                  <input className={`${inputCls} mt-0.5`} type="number" min="1" placeholder="12" value={t.max ?? 12} onChange={(e) => upd({ max: e.target.value })} />
                                </label>
                              </div>
                            </div>
                          );
                        })}
                        <p className="text-[11px] text-slate-500">El <strong>id</strong> no debe cambiarse en actividades ya publicadas: identifica la entrada en las reservas.</p>
                        {priceSummary.hasDeposit ? (
                          <p className="rounded-xl bg-[#1fa5a3]/10 px-3 py-2 text-xs font-semibold text-[#14213d]">
                            💳 Esta actividad cobra solo el <span className="text-[#1fa5a3]">depósito</span> online; el resto se abona in situ.
                          </p>
                        ) : null}
                      </div>
                    ) : (
                      <p className="mt-2 text-xs text-slate-500">Sin entradas: se usará la configuración del servidor.</p>
                    )}
                  </div>

                  <div className="rounded-2xl border border-slate-200 p-4">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Plazas por horario</p>
                    <input className={`${inputCls} mt-2`} type="number" min="1" placeholder="ej. 12" value={form.capacity} onChange={(e) => set('capacity', e.target.value)} />
                    <p className="mt-1 text-[11px] text-slate-500">
                      Necesario para vender online (y para que se apliquen estos precios/depósitos). Los horarios y días bloqueados se editan en la pestaña <strong>Disponibilidad</strong>.
                    </p>
                  </div>
                </div>
              ) : null}

              {/* ---- IMÁGENES ---- */}
              {tab === 'imagenes' ? (
                <div className="space-y-4">
                  <div className="rounded-2xl border border-slate-200 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Imagen principal (portada)</p>
                    <div className="mt-3 flex items-center gap-3">
                      <div className="h-16 w-24 flex-shrink-0 overflow-hidden rounded-xl bg-slate-100">
                        {form.image ? <img src={form.image} alt="" className="h-full w-full object-cover" /> : null}
                      </div>
                      <div className="flex flex-1 flex-wrap gap-2">
                        <button type="button" onClick={() => setPicker('hero')}
                          className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold text-[#14213d] transition hover:border-[#1fa5a3] hover:text-[#1fa5a3]">
                          <ImagePlus className="h-3.5 w-3.5" />Elegir o subir
                        </button>
                        {form.image ? (
                          <button type="button" onClick={() => set('image', '')}
                            className="rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-500 transition hover:border-red-300 hover:text-red-500">Quitar</button>
                        ) : null}
                      </div>
                    </div>
                    <input className={`${inputCls} mt-3`} type="url" placeholder="…o pega una URL" value={form.image} onChange={(e) => set('image', e.target.value)} />
                  </div>

                  <div className="rounded-2xl border border-slate-200 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Galería ({form.gallery.length})</p>
                      <button type="button" onClick={() => setPicker('gallery')}
                        className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-[#14213d] transition hover:border-[#1fa5a3] hover:text-[#1fa5a3]">
                        <Plus className="h-3.5 w-3.5" />Añadir fotos
                      </button>
                    </div>
                    {form.gallery.length ? (
                      <div className="mt-3 space-y-2">
                        {form.gallery.map((url, i) => (
                          <div key={`${url}-${i}`} className="flex items-center gap-2 rounded-xl border border-slate-100 bg-slate-50 p-2">
                            <img src={url} alt="" className="h-10 w-14 flex-shrink-0 rounded-lg object-cover" />
                            <span className="min-w-0 flex-1 truncate text-xs text-slate-600">{url}</span>
                            <button type="button" disabled={i === 0} title="Subir"
                              onClick={() => set('gallery', form.gallery.map((u, j) => (j === i - 1 ? form.gallery[i] : j === i ? form.gallery[i - 1] : u)))}
                              className="rounded-lg border border-slate-200 p-1.5 text-slate-500 disabled:opacity-30 hover:text-[#1fa5a3]">
                              <ArrowLeft className="h-3.5 w-3.5 rotate-90" />
                            </button>
                            <button type="button" disabled={i === form.gallery.length - 1} title="Bajar"
                              onClick={() => set('gallery', form.gallery.map((u, j) => (j === i + 1 ? form.gallery[i] : j === i ? form.gallery[i + 1] : u)))}
                              className="rounded-lg border border-slate-200 p-1.5 text-slate-500 disabled:opacity-30 hover:text-[#1fa5a3]">
                              <ArrowRight className="h-3.5 w-3.5 rotate-90" />
                            </button>
                            <button type="button" title="Quitar" onClick={() => set('gallery', form.gallery.filter((_, j) => j !== i))}
                              className="rounded-lg border border-slate-200 p-1.5 text-red-500 hover:border-red-300">
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-3 text-xs text-slate-500">Sin fotos. La primera de la galería se usa como portada del carrusel.</p>
                    )}
                  </div>

                  <div className="rounded-2xl border border-slate-200 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Vídeo (YouTube)</p>
                    <input className={`${inputCls} mt-3`} placeholder="https://youtube.com/watch?v=... o solo el ID"
                      value={form.video_url} onChange={(e) => set('video_url', e.target.value)} />
                    <p className="mt-1 text-xs text-slate-500">Se muestra embebido en la pestaña "Descripción" de la ficha, sin enlace visible a YouTube. Déjalo vacío para no mostrar vídeo.</p>
                  </div>
                </div>
              ) : null}

              {/* ---- SEO ---- */}
              {tab === 'seo' ? (
                <div className="space-y-4">
                  <div className="rounded-2xl border border-slate-200 p-4">
                    <div className="flex flex-wrap items-center gap-1.5">
                      {LANGS.map((l) => {
                        const filled = Boolean((form.seo_title?.[l.id] || '').trim());
                        return (
                          <button key={l.id} type="button" onClick={() => setLang(l.id)} title={l.name}
                            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition ${
                              lang === l.id ? 'bg-[#14213d] text-white' : 'border border-slate-200 text-slate-600 hover:border-[#1fa5a3] hover:text-[#1fa5a3]'
                            }`}>
                            {l.label}
                            <span className={`h-1.5 w-1.5 rounded-full ${filled ? 'bg-[#1fa5a3]' : lang === l.id ? 'bg-white/40' : 'bg-slate-300'}`} />
                          </button>
                        );
                      })}
                      <span className="ml-auto text-xs text-slate-500">Editando: {LANGS.find((l) => l.id === lang)?.name}</span>
                    </div>

                    <div className="mt-3 space-y-3">
                      <div>
                        <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                          Meta título ({lang.toUpperCase()}) <span className="ml-1 font-normal normal-case tracking-normal text-slate-400">({(form.seo_title?.[lang] || '').length}/65)</span>
                        </label>
                        <input className={`${inputCls} mt-1.5`} maxLength={65}
                          placeholder="Se autogenera desde el título si lo dejas vacío"
                          value={form.seo_title?.[lang] || ''} onChange={(e) => setI18n('seo_title', e.target.value)} />
                      </div>
                      <div>
                        <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                          Meta descripción ({lang.toUpperCase()}) <span className="ml-1 font-normal normal-case tracking-normal text-slate-400">({(form.seo_description?.[lang] || '').length}/158)</span>
                        </label>
                        <textarea className={`${inputCls} mt-1.5 min-h-20`} maxLength={158}
                          placeholder="Se autogenera desde el título, ubicación y precio si lo dejas vacío"
                          value={form.seo_description?.[lang] || ''} onChange={(e) => setI18n('seo_description', e.target.value)} />
                      </div>
                    </div>

                    {/* Google-style search result preview */}
                    <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Vista previa en Google</p>
                      <div className="mt-2 max-w-xl">
                        <p className="text-xs text-slate-600">owawild.com › activity › {form.slug || '...'}</p>
                        <p className="mt-0.5 truncate text-lg text-[#1a0dab]">
                          {form.seo_title?.[lang] || `${form.title?.[lang] || form.title?.es || 'Título de la actividad'} | OWA Wild Adventure`}
                        </p>
                        <p className="mt-0.5 line-clamp-2 text-sm text-slate-600">
                          {form.seo_description?.[lang] || 'Se generará automáticamente a partir del título, la ubicación y el precio.'}
                        </p>
                      </div>
                    </div>

                    <p className="mt-3 text-xs text-slate-500">Los campos vacíos se rellenan solos con título + ubicación + precio, en cada idioma, así ninguna ficha se queda sin SEO.</p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Palabra clave (interna, no se muestra)</p>
                    <input className={`${inputCls} mt-1.5`} placeholder="ej. kayak en anfi del mar"
                      value={form.seo_keyphrase} onChange={(e) => set('seo_keyphrase', e.target.value)} />
                    <p className="mt-1 text-xs text-slate-500">Solo como referencia interna a la hora de redactar el título y la descripción.</p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">URL canónica (opcional)</p>
                    <input className={`${inputCls} mt-1.5`} type="url" placeholder="https://owawild.com/activity/..."
                      value={form.canonical_url} onChange={(e) => set('canonical_url', e.target.value)} />
                    <p className="mt-1 text-xs text-slate-500">Solo rellénalo si esta actividad duplica el contenido de otra URL y quieres decirle a Google cuál es la "oficial".</p>
                  </div>

                  <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <input type="checkbox" checked={form.noindex} onChange={(e) => set('noindex', e.target.checked)} className="h-4 w-4" />
                    <span>
                      <span className="block text-sm font-semibold text-[#14213d]">Ocultar de buscadores (noindex)</span>
                      <span className="block text-xs text-slate-500">La ficha sigue visible en la web, pero le pedimos a Google que no la indexe.</span>
                    </span>
                  </label>
                </div>
              ) : null}

              <button type="submit" disabled={saveMutation.isPending}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1fa5a3] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#188b89] disabled:opacity-60">
                <Plus className="h-4 w-4" />{saveMutation.isPending ? 'Guardando…' : editingSlug ? 'Guardar cambios' : 'Crear actividad'}
              </button>
            </form>
          ) : null}

          {/* ---- DISPONIBILIDAD ---- */}
          {tab === 'disponibilidad' ? (
            <div className="mt-5">
              {editingSlug ? (
                <AvailabilityEditor key={editingSlug} slug={editingSlug} activity={activityDoc} />
              ) : (
                <p className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">Guarda la actividad primero (pestaña General → Crear actividad) para configurar horarios y cupo.</p>
              )}
            </div>
          ) : null}

          {/* ---- EXTRAS ---- */}
          {tab === 'extras' ? (
            <div className="mt-5">
              {editingSlug ? (
                <AddonsEditor key={editingSlug} slug={editingSlug} activity={activityDoc} />
              ) : (
                <p className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">Guarda la actividad primero para añadir extras de pago.</p>
              )}
            </div>
          ) : null}
        </section>
      )}

      <ImagePicker
        open={picker !== null}
        multiple={picker === 'gallery'}
        title={picker === 'gallery' ? 'Añadir fotos a la galería' : 'Imagen principal'}
        onClose={() => setPicker(null)}
        onSelect={(value) => {
          if (picker === 'gallery') {
            const urls = Array.isArray(value) ? value : [value];
            set('gallery', [...form.gallery, ...urls.filter((u) => !form.gallery.includes(u))]);
          } else {
            set('image', value);
          }
          setPicker(null);
        }}
      />
    </AdminSection>
  );
}
